import { useState, useEffect, useCallback, useMemo } from 'react';
import api from '@/lib/api';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '@/components/ui/dialog';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '@/components/ui/alert-dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Loader2, Plus, Pencil, Trash2, Search, Save, RefreshCcw } from 'lucide-react';

/**
 * Schema-driven editable table for any register (piping or structural).
 * Backend contract:
 *   GET  /v1/projects/{pid}/registers/{registerId}/schema
 *   GET  /v1/projects/{pid}/registers/{registerId}/rows?q=&skip=&limit=
 *   POST /v1/projects/{pid}/registers/{registerId}/rows
 *   PUT  /v1/projects/{pid}/registers/{registerId}/rows/{row_id}
 *   DELETE .../rows/{row_id}
 */
function pluck(obj, dotted) {
  if (!obj) return undefined;
  if (!dotted.includes('.')) return obj[dotted];
  return dotted.split('.').reduce((acc, k) => (acc && typeof acc === 'object' ? acc[k] : undefined), obj);
}

function setNested(obj, dotted, value) {
  if (!dotted.includes('.')) { obj[dotted] = value; return; }
  const parts = dotted.split('.');
  let cur = obj;
  for (let i = 0; i < parts.length - 1; i++) {
    if (typeof cur[parts[i]] !== 'object' || cur[parts[i]] === null) cur[parts[i]] = {};
    cur = cur[parts[i]];
  }
  cur[parts.at(-1)] = value;
}

function formatCell(value, type) {
  if (value === null || value === undefined || value === '') return <span className="text-muted-foreground">—</span>;
  if (type === 'date') return String(value).slice(0, 10);
  if (type === 'number' && typeof value === 'number') {
    return Number.isInteger(value) ? value.toLocaleString() : value.toLocaleString(undefined, { maximumFractionDigits: 2 });
  }
  return String(value);
}

export default function EditableRegister({ projectId, registerId, onClose }) {
  const [schema, setSchema] = useState(null);
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [query, setQuery] = useState('');
  const [debouncedQ, setDebouncedQ] = useState('');
  const [editing, setEditing] = useState(null); // { row, isNew }
  const [confirmDelete, setConfirmDelete] = useState(null);
  const readOnlyExisting = !!schema?.readOnlyExisting;

  const fetchAll = useCallback(async () => {
    setLoading(true);
    try {
      const [s, r] = await Promise.all([
        api.get(`/v1/projects/${projectId}/registers/${registerId}/schema`),
        api.get(`/v1/projects/${projectId}/registers/${registerId}/rows`, { params: { q: debouncedQ, limit: 500 } }),
      ]);
      setSchema(s.data);
      setRows(r.data.rows || []);
      setTotal(r.data.total || 0);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to load register');
    }
    setLoading(false);
  }, [projectId, registerId, debouncedQ]);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  useEffect(() => {
    const t = setTimeout(() => setDebouncedQ(query.trim()), 300);
    return () => clearTimeout(t);
  }, [query]);

  const columns = schema?.columns || [];
  const visibleColumns = useMemo(() => columns, [columns]);

  const openNew = () => {
    const blank = {};
    columns.forEach(c => setNested(blank, c.field, c.type === 'number' ? '' : ''));
    setEditing({ row: blank, isNew: true });
  };

  const openEdit = (row) => setEditing({ row: JSON.parse(JSON.stringify(row)), isNew: false });

  const saveRow = async () => {
    if (!editing) return;
    // Client-side required validation
    for (const c of columns) {
      if (c.required && !pluck(editing.row, c.field) && pluck(editing.row, c.field) !== 0) {
        toast.error(`${c.header} is required`); return;
      }
    }
    setSaving(true);
    try {
      const payload = { ...editing.row };
      // strip _id/id when creating
      delete payload._id; if (editing.isNew) delete payload.id;
      if (editing.isNew) {
        await api.post(`/v1/projects/${projectId}/registers/${registerId}/rows`, payload);
        toast.success('Row created');
      } else {
        await api.put(`/v1/projects/${projectId}/registers/${registerId}/rows/${editing.row.id}`, payload);
        toast.success('Row updated');
      }
      setEditing(null);
      fetchAll();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Save failed');
    }
    setSaving(false);
  };

  const doDelete = async () => {
    if (!confirmDelete) return;
    try {
      await api.delete(`/v1/projects/${projectId}/registers/${registerId}/rows/${confirmDelete.id}`);
      toast.success('Row deleted');
      setConfirmDelete(null);
      fetchAll();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Delete failed');
    }
  };

  if (loading && !schema) return <div className="flex items-center justify-center h-64 text-muted-foreground"><Loader2 className="animate-spin mr-2 h-5 w-5"/>Loading register…</div>;
  if (!schema) return <div className="p-6 text-destructive">Register not found</div>;

  return (
    <div className="flex flex-col h-full">
      {/* Toolbar */}
      <div className="flex items-center gap-3 p-4 border-b bg-muted/30" data-testid={`register-toolbar-${registerId}`}>
        <div className="flex-1">
          <h2 className="text-lg font-semibold tracking-tight">{schema.title}</h2>
          <p className="text-xs text-muted-foreground">
            {total.toLocaleString()} {total === 1 ? 'row' : 'rows'} · {readOnlyExisting ? 'Update existing rows only' : 'Editable in-platform (Excel remains as fallback)'}
          </p>
        </div>
        <div className="relative">
          <Search className="absolute left-2.5 top-2.5 h-3.5 w-3.5 text-muted-foreground"/>
          <Input value={query} onChange={e => setQuery(e.target.value)} placeholder="Search…" className="pl-8 h-9 w-64" data-testid={`register-search-${registerId}`}/>
        </div>
        <Button variant="outline" size="sm" onClick={fetchAll} data-testid={`register-refresh-${registerId}`}><RefreshCcw className="h-3.5 w-3.5 mr-1.5"/>Refresh</Button>
        {!readOnlyExisting && (
          <Button size="sm" onClick={openNew} data-testid={`register-add-${registerId}`}><Plus className="h-4 w-4 mr-1"/>Add Row</Button>
        )}
      </div>

      {/* Grid */}
      <div className="flex-1 overflow-auto">
        <Table data-testid={`register-table-${registerId}`}>
          <TableHeader className="sticky top-0 bg-background z-10">
            <TableRow>
              {visibleColumns.map(c => (
                <TableHead key={c.field} className="whitespace-nowrap text-[11px] uppercase tracking-wide font-semibold" style={{ minWidth: c.width || 120 }}>
                  {c.header}{c.required && <span className="text-red-500 ml-1">*</span>}
                </TableHead>
              ))}
              <TableHead className="text-right sticky right-0 bg-background z-10" style={{ minWidth: 100 }}>Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {rows.length === 0 && !loading && (
              <TableRow><TableCell colSpan={visibleColumns.length + 1} className="text-center py-10 text-muted-foreground text-sm">No rows yet — click <b>Add Row</b> or use Excel Import above.</TableCell></TableRow>
            )}
            {rows.map(r => (
              <TableRow key={r.id} className="hover:bg-muted/40" data-testid={`register-row-${registerId}-${r.id}`}>
                {visibleColumns.map(c => {
                  const raw = pluck(r, c.field);
                  const shown = formatCell(raw, c.type);
                  return (
                    <TableCell key={c.field}
                      className={c.type === 'number' ? 'font-mono text-right tabular-nums' : 'align-top'}
                      style={c.type === 'number' ? undefined
                                                 : { maxWidth: 320, whiteSpace: 'normal', wordBreak: 'break-word', lineHeight: 1.25 }}
                      title={raw !== undefined && raw !== null ? String(raw) : undefined}>
                      {shown}
                    </TableCell>
                  );
                })}
                <TableCell className="text-right sticky right-0 bg-background">
                  <Button variant="ghost" size="icon" onClick={() => openEdit(r)} data-testid={`register-edit-${registerId}-${r.id}`}><Pencil className="h-3.5 w-3.5"/></Button>
                  <Button variant="ghost" size="icon" onClick={() => setConfirmDelete(r)} data-testid={`register-delete-${registerId}-${r.id}`}><Trash2 className="h-3.5 w-3.5 text-destructive"/></Button>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>

      {/* Add / Edit dialog */}
      <Dialog open={!!editing} onOpenChange={o => !o && setEditing(null)}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto" data-testid={`register-form-${registerId}`}>
          <DialogHeader>
            <DialogTitle>{editing?.isNew ? 'Add' : 'Edit'} · {schema.title}</DialogTitle>
            <DialogDescription>All fields marked <span className="text-red-500">*</span> are required.</DialogDescription>
          </DialogHeader>
          <div className="grid grid-cols-2 gap-4 py-2">
            {columns.map(c => {
              const val = pluck(editing?.row || {}, c.field);
              const disabled = c.readOnly || (!editing?.isNew && c.field === schema.keyField && schema.readOnlyExisting);
              const onChange = (v) => {
                setEditing(prev => {
                  const next = { ...prev, row: { ...prev.row } };
                  setNested(next.row, c.field, v);
                  return next;
                });
              };
              return (
                <div key={c.field} className={c.type === 'text' && (c.field.includes('description') || c.field.includes('Name')) ? 'col-span-2' : 'col-span-1'}>
                  <label className="text-xs font-medium text-muted-foreground uppercase tracking-wide">{c.header}{c.required && <span className="text-red-500 ml-1">*</span>}</label>
                  {c.type === 'select' ? (
                    <Select value={val || ''} onValueChange={onChange} disabled={disabled}>
                      <SelectTrigger className="h-9 mt-1" data-testid={`register-field-${registerId}-${c.field}`}><SelectValue placeholder="Select…"/></SelectTrigger>
                      <SelectContent>{(c.options || []).map(o => <SelectItem key={o} value={o}>{o}</SelectItem>)}</SelectContent>
                    </Select>
                  ) : c.type === 'date' ? (
                    <Input type="date" value={(val || '').slice(0, 10)} onChange={e => onChange(e.target.value)} disabled={disabled} className="h-9 mt-1" data-testid={`register-field-${registerId}-${c.field}`}/>
                  ) : c.type === 'number' ? (
                    <Input type="number" step="any" value={val ?? ''} onChange={e => onChange(e.target.value === '' ? '' : Number(e.target.value))} disabled={disabled} className="h-9 mt-1 font-mono" data-testid={`register-field-${registerId}-${c.field}`}/>
                  ) : (
                    <Input value={val ?? ''} onChange={e => onChange(e.target.value)} disabled={disabled} className="h-9 mt-1" data-testid={`register-field-${registerId}-${c.field}`}/>
                  )}
                </div>
              );
            })}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditing(null)}>Cancel</Button>
            <Button onClick={saveRow} disabled={saving} data-testid={`register-save-${registerId}`}>
              {saving ? <Loader2 className="h-4 w-4 mr-2 animate-spin"/> : <Save className="h-4 w-4 mr-2"/>}
              {editing?.isNew ? 'Create' : 'Save changes'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete confirmation */}
      <AlertDialog open={!!confirmDelete} onOpenChange={o => !o && setConfirmDelete(null)}>
        <AlertDialogContent data-testid={`register-delete-confirm-${registerId}`}>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete this row?</AlertDialogTitle>
            <AlertDialogDescription>
              This will permanently remove <b>{pluck(confirmDelete || {}, schema.keyField) || 'this row'}</b> from <b>{schema.title}</b>. This cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid={`register-delete-cancel-${registerId}`}>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={doDelete} className="bg-destructive hover:bg-destructive/90" data-testid={`register-delete-confirm-btn-${registerId}`}>Delete</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
